import React from 'react';
import { useTranslation } from 'react-i18next';
import * as format from '../../utils/formatters';
import shortCurrency from '../../utils/shortCurrency';
import { VerbalMentionRsp } from '@app/types/api';
import Page from './Page';
import ExposureCard from './ExposureCard';
import VerbalMentionContextBox from './VerbalMentionContextBox';

interface BroadcastVerbalMentionPDFProps {
    broadcastName: string;
    verbalMention: VerbalMentionRsp;
    currencyCode: string;
    videoUrl: string;
}

function BroadcastVerbalMentionPDF({
    broadcastName,
    verbalMention,
    currencyCode,
    videoUrl,
}: BroadcastVerbalMentionPDFProps) {
    const { t } = useTranslation('pdf');

    const vmSample = {
        mention: verbalMention.mention,
        videoUrl: videoUrl,
    };

    const containerStyle = {
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse' as const,
    };

    const headerCellStyle = {
        fontWeight: '500' as const,
        fontSize: '14px',
        color: '#333',
    };

    const dataCellStyle = {
        textAlign: 'center' as const,
        fontSize: '14px',
        color: '#666',
    };

    const metricsTableStyle = {
        width: '100%',
        borderCollapse: 'collapse' as const,
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
    };

    const metricsHeaderStyle = {
        textAlign: 'center' as const,
        padding: '6px',
        fontWeight: '600' as const,
        fontSize: '14px',
        color: '#333',
        border: '1px solid #e0e0e0',
    };

    const metricsCellStyle = {
        textAlign: 'center' as const,
        padding: '6px',
        fontSize: '14px',
        color: '#666',
        border: '1px solid #e0e0e0',
    };

    const formatImpressions = () => {
        return verbalMention.impressions === 0
            ? '-'
            : format.abbreviatedDecimal(verbalMention.impressions);
    };

    const formatDuration = () => {
        return verbalMention.duration === 0
            ? '-'
            : `${verbalMention.duration}s`;
    };

    const formatMvpScore = () => {
        return verbalMention.averageMvpScore === 0
            ? '-'
            : format.percentage(verbalMention.averageMvpScore);
    };

    const formatSponsorMediaValue = () => {
        return verbalMention.sponsorMediaValue === 0
            ? '-'
            : shortCurrency(verbalMention.sponsorMediaValue, 1, currencyCode);
    };

    return (
        <div style={containerStyle}>
            <Page>
                <ExposureCard
                    broadcastName={broadcastName}
                    verbalMentionsExample={vmSample}
                >
                    <VerbalMentionContextBox
                        context={verbalMention.context}
                        mention={verbalMention.mention}
                    />

                    <table style={tableStyle}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <th style={headerCellStyle}>
                                    {t('verbalMention.rightsholder')}
                                </th>
                                <th style={headerCellStyle}>
                                    {t('verbalMention.sponsor')}
                                </th>
                                <th style={headerCellStyle}>
                                    {t('verbalMention.placement')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={dataCellStyle}>
                                    {verbalMention.rightsholder.name}
                                </td>
                                <td style={dataCellStyle}>
                                    {verbalMention.sponsor.name}
                                </td>
                                <td style={dataCellStyle}>
                                    {verbalMention.placement.name}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div>
                        <table style={metricsTableStyle}>
                            <thead>
                                <tr style={{ backgroundColor: '#E6F4FF' }}>
                                    <th style={metricsHeaderStyle}>
                                        {t('verbalMention.impressions')}
                                    </th>
                                    <th style={metricsHeaderStyle}>
                                        {t('verbalMention.duration')}
                                    </th>
                                    <th style={metricsHeaderStyle}>
                                        {t('verbalMention.mvp')}
                                    </th>
                                    <th style={metricsHeaderStyle}>
                                        {t('verbalMention.smv')}
                                    </th>
                                    <th style={metricsHeaderStyle}>
                                        {t('verbalMention.network')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={metricsCellStyle}>
                                        {formatImpressions()}
                                    </td>
                                    <td style={metricsCellStyle}>
                                        {formatDuration()}
                                    </td>
                                    <td style={metricsCellStyle}>
                                        {formatMvpScore()}
                                    </td>
                                    <td style={metricsCellStyle}>
                                        {formatSponsorMediaValue()}
                                    </td>
                                    <td style={metricsCellStyle}>
                                        {verbalMention.network.name}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ExposureCard>
            </Page>
        </div>
    );
}

export default BroadcastVerbalMentionPDF;
